// 🏗️ ENTERPRISE UPLOAD CONSTANTS - SINGLE SOURCE OF TRUTH
// Replaces all hardcoded values and duplicate config files

export const UPLOAD_CONFIG = {
  // File size limits (10GB max - enterprise standard for large video files)
  MAX_FILE_SIZE_BYTES: 10 * 1024 * 1024 * 1024, // 10GB absolute maximum


  // Multipart configuration (for ALL files - unified upload method)
  MULTIPART_CHUNK_SIZE: 50 * 1024 * 1024, // 50MB chunks (matches actual implementation)
  MAX_CONCURRENT_CHUNKS: 2, // Parallel upload limit (reduced for network stability)

  // Timeout configuration
  BROWSER_UPLOAD_TIMEOUT: 15 * 60 * 1000, // 15 minutes for browser uploads
  CHUNK_UPLOAD_TIMEOUT: 5 * 60 * 1000,    // 5 minutes per chunk

  // Retry configuration
  MAX_UPLOAD_RETRIES: 3,
  RETRY_DELAY_BASE: 1000, // 1 second base delay

  // Progress update frequency
  PROGRESS_UPDATE_INTERVAL: 500, // 500ms

  // Storage configuration
  STORAGE_BUCKET: 'videoshareweb-family',
  STORAGE_PATH_PREFIX: 'videos'
}

// Supported video file formats
export const VIDEO_FORMATS = {
  // High compatibility - work without issues
  HIGH_COMPATIBILITY: ['.mp4', '.mov', '.webm'],

  // Medium compatibility - may need conversion warnings
  MEDIUM_COMPATIBILITY: ['.avi', '.wmv', '.flv'],

  // Lower compatibility - upload fine, may need conversion
  LOWER_COMPATIBILITY: ['.mkv', '.m4v', '.3gp', '.ogv'],

  // All supported formats combined
  ALL_SUPPORTED: ['.mp4', '.mov', '.webm', '.avi', '.wmv', '.flv', '.mkv', '.m4v', '.3gp', '.ogv']
}

// MIME type mappings
export const VIDEO_MIME_TYPES = {
  '.mp4': 'video/mp4',
  '.mov': 'video/quicktime',
  '.webm': 'video/webm',
  '.avi': 'video/x-msvideo',
  '.wmv': 'video/x-ms-wmv',
  '.flv': 'video/x-flv',
  '.mkv': 'video/x-matroska',
  '.m4v': 'video/x-m4v',
  '.3gp': 'video/3gpp',
  '.ogv': 'video/ogg'
}

// Upload method decision helper - UNIFIED TO MULTIPART ONLY
export const getUploadMethod = (fileSize) => {
  // ENTERPRISE DECISION: Always use multipart for consistent storage pattern
  // Eliminates videos/uid/filename vs videos/timestamp_filename confusion
  return 'multipart' // All files use multipart for single storage pattern
}

// File size formatting helper
export const formatFileSize = (bytes) => {
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  if (bytes === 0) return '0 Bytes'
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i]
}

// File extension helper
export const getFileExtension = (filename) => {
  const lastDot = filename.lastIndexOf('.')
  return lastDot !== -1 ? filename.substring(lastDot).toLowerCase() : ''
}

// Validation helpers
export const isValidVideoFile = (file) => {
  const extension = getFileExtension(file.name)
  return VIDEO_FORMATS.ALL_SUPPORTED.includes(extension)
}

export const isFileSizeValid = (fileSize) => {
  return fileSize <= UPLOAD_CONFIG.MAX_FILE_SIZE_BYTES
}

// Progress calculation helper
export const calculateProgress = (loaded, total) => {
  if (!total || total === 0) return 0
  return Math.round((loaded / total) * 100)
}

// Enterprise compliance verification
export const ENTERPRISE_VALIDATION = {
  // This file ensures all upload size/format logic comes from ONE place
  SINGLE_SOURCE_OF_TRUTH: true,
  REPLACES_HARDCODED_VALUES: [
    '4 * 1024 * 1024 * 1024', // Old 4GB threshold (now 1GB)
    '500 * 1024 * 1024',      // Old 500MB threshold
    '100 * 1024 * 1024',      // Chunk sizes
    'hardcoded timeout values',
    'scattered format lists'
  ],
  ELIMINATES_CONFIG_FILES: [
    'videoConstants.js (partially)',
    'hardcoded values in VideoContext.jsx',
    'hardcoded values in components'
  ]
}